"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, navigationMenuTriggerStyle } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
function DesktopMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden lg:flex items-center gap-4">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-4 w-20 bg-white/10 animate-pulse rounded" />)}
      </div>;
  }
  return <NavigationMenu className="hidden lg:flex">
      <NavigationMenuList className="gap-1">
        {menuItems.filter(item => item.label !== "Intake Form").map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.label === "Partners" ? "/PartnersPage" : item.label === "Equipment Capabilities" ? "/#equipment-field-capabilities" : item.href || "#"} className={cn(navigationMenuTriggerStyle(), "bg-transparent text-white hover:bg-white/10 hover:text-white focus:bg-white/10 focus:text-white uppercase text-sm font-medium tracking-wider transition-colors")}>
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [isOpen, setIsOpen] = useState(false);
  return <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="lg:hidden text-white hover:bg-white/10 hover:text-white" aria-label="Open menu">
          <Menu className="h-6 w-6" />
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-[300px] bg-background border-l border-white/10 p-0">
        <SheetHeader className="p-6 border-b border-white/10">
          <SheetTitle className="text-left text-white">Menu</SheetTitle>
        </SheetHeader>
        <div className="flex flex-col py-6 px-6 gap-4">
          {loading ? <div className="flex flex-col gap-4">
              {[1, 2, 3, 4].map(i => <div key={i} className="h-6 w-full bg-white/10 animate-pulse rounded" />)}
            </div> : menuItems.filter(item => item.label !== "Intake Form").map((item, i) => <div key={item.id} data-index={i} className="flex flex-col border-b border-white/5 pb-4 last:border-0">
                <SheetClose asChild>
                  <Link to={item.label === "Partners" ? "/PartnersPage" : item.label === "Equipment Capabilities" ? "/#equipment-field-capabilities" : item.href || "#"} className="text-lg font-medium text-white hover:text-primary transition-colors uppercase tracking-wide">
                    {item.label}
                  </Link>
                </SheetClose>
                {}
                {item.children && item.children.length > 0 && <div className="flex flex-col gap-3 mt-3 pl-4 border-l border-white/10">
                    {item.children.map((child, idx) => <SheetClose key={child.id} asChild data-index={idx}>
                        <Link to={child.label === "Partners" ? "/PartnersPage" : child.label === "Equipment Capabilities" ? "/#equipment-field-capabilities" : child.href || "#"} className="text-sm text-white/70 hover:text-white transition-colors">
                          {child.label}
                        </Link>
                      </SheetClose>)}
                  </div>}
              </div>)}
          <div className="mt-4">
            <SheetClose asChild>
              <Button asChild className="w-full rounded-full bg-primary text-white hover:bg-primary/90 h-12 text-base uppercase tracking-wide">
                <Link to="/IntakeFormPage">Intake form</Link>
              </Button>
            </SheetClose>
          </div>
        </div>
      </SheetContent>
    </Sheet>;
}
function HeaderContent() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header className={cn("fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b border-transparent", isScrolled ? "bg-background/80 backdrop-blur-md border-white/5 shadow-lg py-3" : "bg-background py-5")}>
      <div className="container mx-auto px-4 md:px-8 flex items-center justify-between">
        {}
        <Link to="/" className="flex-shrink-0 z-50">
          <img src="https://fluent-gnu.10web.cloud/wp-content/uploads/2026/02/Asset-1-1.png" alt="QST Geotechnical" className="h-10 w-auto object-contain" />
        </Link>

        {}
        <div className="hidden lg:flex items-center gap-8">
          <DesktopMenu />
          <Button asChild className="rounded-full bg-primary text-white hover:bg-primary/90 px-8 h-10 uppercase text-xs font-bold tracking-widest shadow-md transition-transform hover:scale-105">
            <Link to="/IntakeFormPage">Intake form</Link>
          </Button>
        </div>

        {}
        <MobileMenu />
      </div>
    </header>;
}
export default function SiteNavigation() {
  return <section id="site-navigation" className="dark bg-background text-foreground">
      <MenuProvider menu_id="44" className="w-full">
        <HeaderContent />
      </MenuProvider>
      {}
      <div className="h-20" />
    </section>;
}